import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { fetchAnalysisHistory } from "./axiosFetchers";

function flatten(value, prefix = "") {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => flatten(item, `${prefix}[${index}]`));
  }
  if (typeof value === "object") {
    return Object.entries(value).flatMap(([key, item]) =>
      flatten(item, prefix ? `${prefix}.${key}` : key)
    );
  }
  return [[prefix, String(value)]];
}

function formatDate(value) {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function changedInputs(currentInputs, previousInputs) {
  const current = new Map(flatten(currentInputs || {}));
  const previous = new Map(flatten(previousInputs || {}));
  return [...new Set([...current.keys(), ...previous.keys()])]
    .sort()
    .flatMap((key) => {
      const before = previous.get(key) ?? "Not provided";
      const after = current.get(key) ?? "Not provided";
      return before === after
        ? []
        : [
            [`${key} - previous`, before],
            [`${key} - selected`, after],
          ];
    });
}

export default function AnalysisHistory({ cropyearId, currentVersionId, user }) {
  const [analysisRuns, setAnalysisRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setAnalysisRuns([]);
    fetchAnalysisHistory(cropyearId, user)
      .then((runs) => {
        if (!cancelled) {
          setAnalysisRuns(
            (runs || []).filter(
              (run) => run.indicators?.ghg_source === "holos"
            )
          );
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Analysis history could not be loaded.");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cropyearId, user]);

  const selectedRows = useMemo(
    () => selected?.rows || flatten(selected?.inputs || {}),
    [selected]
  );

  return (
    <section className="mt-8 border-t border-slate-200 pt-6">
      <div className="mb-4 flex items-center gap-2">
        <HistoryIcon color="action" />
        <h2 className="text-[22px] font-semibold text-slate-800">Analysis history</h2>
      </div>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? (
        <div className="flex items-center gap-3 py-5 text-sm text-slate-600">
          <CircularProgress size={20} /> Loading analysis history...
        </div>
      ) : null}

      {!loading ? <TableContainer className="border border-slate-200">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Generated</TableCell>
              <TableCell>Crop-year version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>GHG source</TableCell>
              <TableCell>GHG result</TableCell>
              <TableCell align="right">Inputs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analysisRuns.map((run, index) => {
              const ghgIndex = run.labels?.indexOf("Greenhouse Gas") ?? -1;
              const previousRun = analysisRuns[index + 1];
              const isCurrent =
                currentVersionId != null &&
                String(run.crop_year_version_id) === String(currentVersionId);
              return (
                <TableRow key={run.id} hover>
                  <TableCell>{formatDate(run.created_at)}</TableCell>
                  <TableCell>{run.crop_year_version_id || "Draft"}</TableCell>
                  <TableCell>
                    <Chip
                      color={isCurrent ? "success" : "default"}
                      label={isCurrent ? "Current inputs" : "Prior inputs"}
                      size="small"
                      variant={isCurrent ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Holos"
                    />
                  </TableCell>
                  <TableCell>{ghgIndex >= 0 ? run.values?.[ghgIndex] : "-"}</TableCell>
                  <TableCell align="right">
                    <div className="flex justify-end gap-1">
                      {previousRun ? (
                        <Tooltip title="Compare with previous run">
                          <IconButton
                            size="small"
                            aria-label="Compare frozen analysis inputs"
                            onClick={() =>
                              setSelected({
                                title: `Input changes for ${formatDate(run.created_at)}`,
                                rows: changedInputs(
                                  run.input_snapshot,
                                  previousRun.input_snapshot
                                ),
                              })
                            }
                          >
                            <CompareArrowsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="View frozen inputs">
                        <IconButton
                          size="small"
                          aria-label="View frozen analysis inputs"
                          onClick={() =>
                            setSelected({
                              title: `Analysis from ${formatDate(run.created_at)}`,
                              inputs: run.input_snapshot,
                            })
                          }
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {!analysisRuns.length ? (
              <TableRow>
                <TableCell colSpan={6}>No previous analysis runs.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer> : null}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>{selected?.title}</DialogTitle>
        <Divider />
        <DialogContent>
          <Table size="small">
            <TableBody>
              {!selectedRows.length ? (
                <TableRow>
                  <TableCell>No input differences were found.</TableCell>
                </TableRow>
              ) : null}
              {selectedRows.map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ width: "44%", fontWeight: 600 }}>{label}</TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}
